import React, { useState, useEffect } from 'react';
import { ReportsProps } from '../../../types/expertise-detail.types';
import { getAffaireReports, downloadFile } from '../../../api/expertise-detail.api';
import { formatDate } from '../../../utils/formatters';
import { Button } from '../../../../../../../ui/components/atoms/Button';
import { Loading } from '@/ui/components/atoms/Loading';

export const Reports: React.FC<ReportsProps> = ({ affaireId, reports: initialReports }) => {
  const [reports, setReports] = useState(initialReports);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchReports();
  }, [affaireId]);

  const fetchReports = async () => {
    if (!affaireId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await getAffaireReports(affaireId);
      
      if (response.success && response.data) {
        // Map the API response to our report structure
        const mappedReports = {
          intermediate: {
            available: response.data.reports.prerapport && response.data.reports.prerapport.length > 0,
            fileName: response.data.reports.prerapport?.[0]?.fileName || null,
            date: response.data.reports.prerapport?.[0]?.date || null,
            downloadUrl: response.data.reports.prerapport?.[0]?.downloadUrl,
          },
          final: {
            available: response.data.reports.expertise && response.data.reports.expertise.length > 0,
            fileName: response.data.reports.expertise?.[0]?.fileName || null,
            date: response.data.reports.expertise?.[0]?.date || null,
            downloadUrl: response.data.reports.expertise?.[0]?.downloadUrl,
          },
        };
        setReports(mappedReports);
      }
    } catch (err) {
      console.error('Error fetching reports:', err);
      setError('Erreur lors du chargement des rapports');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (reportType: 'intermediate' | 'final') => {
    const report = reportType === 'intermediate' ? reports.intermediate : reports.final;
    
    if (!report.downloadUrl || !report.fileName) {
      setError('Informations de téléchargement non disponibles');
      return;
    }

    setDownloading(prev => new Set(prev).add(reportType));
    setError(null);

    try {
      await downloadFile(report.downloadUrl, report.fileName);
    } catch (err) {
      console.error('Error downloading report:', err);
      setError(`Erreur lors du téléchargement du ${reportType === 'intermediate' ? 'rapport intermédiaire' : 'rapport final'}`);
    } finally {
      setDownloading(prev => {
        const newSet = new Set(prev);
        newSet.delete(reportType);
        return newSet;
      });
    }
  };

  if (loading) {
    return (
      <div className="reports-container loading">
        <Loading size="medium" text="Chargement des rapports..." />
      </div>
    );
  }

  return (
    <div className="reports-container">
      <div className="reports-header">
        <h3>Rapports d'expertise</h3>
        <Button 
          variant="ghost" 
          size="small" 
          onClick={fetchReports} 
          title="Actualiser"
          icon={
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M21 2v6h-6M3 12a9 9 0 0115-6.7L21 8M3 22v-6h6M21 12a9 9 0 01-15 6.7L3 16" 
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          }
        />
      </div>

      {error && (
        <div className="error-message">
          <span>⚠️ {error}</span>
        </div>
      )}

      <div className="reports-grid">
        {/* Intermediate Report */}
        <div className={`report-card ${reports.intermediate.available ? 'available' : 'unavailable'}`}>
          <div className="report-icon">
            {reports.intermediate.available ? (
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" 
                  stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" 
                  stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ) : (
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="#9CA3AF" strokeWidth="2"/>
                <path d="M12 6v6l4 2" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            )}
          </div>
          <div className="report-info">
            <h4>Rapport intermédiaire</h4>
            {reports.intermediate.available ? (
              <>
                {reports.intermediate.fileName && (
                  <p className="report-filename">{reports.intermediate.fileName}</p>
                )}
                {reports.intermediate.date && (
                  <p className="report-date">Généré le {formatDate(reports.intermediate.date)}</p>
                )}
              </>
            ) : (
              <p className="report-status">Sera disponible à 70% d'avancement</p>
            )}
          </div>
          <div className="report-action">
            {reports.intermediate.available ? (
              <Button
                variant="outline-dark"
                size="small"
                onClick={() => handleDownload('intermediate')}
                disabled={downloading.has('intermediate')}
                loading={downloading.has('intermediate')}
                icon={
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" 
                      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                }
              >
                {downloading.has('intermediate') ? 'Téléchargement...' : 'Télécharger'}
              </Button>
            ) : (
              <span className="status-badge pending">Non disponible</span>
            )}
          </div>
        </div>

        {/* Final Report */}
        <div className={`report-card ${reports.final.available ? 'available' : 'unavailable'}`}>
          <div className="report-icon">
            {reports.final.available ? (
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" 
                  stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14 2v6h6M9 15l2 2 4-4" 
                  stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ) : (
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="#9CA3AF" strokeWidth="2"/>
                <path d="M12 6v6l4 2" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            )}
          </div>
          <div className="report-info">
            <h4>Rapport final</h4>
            {reports.final.available ? (
              <>
                {reports.final.fileName && (
                  <p className="report-filename">{reports.final.fileName}</p>
                )}
                {reports.final.date && (
                  <p className="report-date">Généré le {formatDate(reports.final.date)}</p>
                )}
              </>
            ) : (
              <p className="report-status">Sera disponible à la fin de l'expertise</p>
            )}
          </div>
          <div className="report-action">
            {reports.final.available ? (
              <Button
                variant="outline-dark"
                size="small"
                onClick={() => handleDownload('final')}
                disabled={downloading.has('final')}
                loading={downloading.has('final')}
                icon={
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" 
                      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                }
              >
                {downloading.has('final') ? 'Téléchargement...' : 'Télécharger'}
              </Button>
            ) : (
              <span className="status-badge pending">Non disponible</span>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .reports-container {
          padding: 28px;
          background: white;
          border-radius: 20px;
          border: 1px solid #E5E7EB;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06);
        }

        .reports-container.loading {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 200px;
        }


        .reports-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .reports-header h3 {
          margin: 0;
          font-size: 16px;
          font-weight: 700;
          color: #002035;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .reports-header h3::before {
          content: '';
          width: 4px;
          height: 20px;
          background: linear-gradient(180deg, #10B981 0%, #059669 100%);
          border-radius: 2px;
        }

        .refresh-button {
          padding: 4px 8px;
          background: white;
          border: 1px solid #E5E7EB;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.2s ease;
        }

        .refresh-button:hover {
          background: #F3F4F6;
          transform: rotate(180deg);
        }

        .error-message {
          padding: 12px;
          background: #FEF2F2;
          border: 1px solid #FECACA;
          border-radius: 6px;
          color: #DC2626;
          font-size: 14px;
          margin-bottom: 16px;
        }

        .reports-grid {
          display: grid;
          gap: 16px;
        }

        .report-card {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 24px;
          background: linear-gradient(135deg, #FFFFFF 0%, #F8F9FA 100%);
          border: 1px solid #E5E7EB;
          border-radius: 16px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .report-card:hover {
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          transform: translateY(-1px);
        }

        .report-card.available {
          border-color: #10B981;
          background: linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%);
        }

        .report-card.unavailable {
          opacity: 0.7;
        }

        .report-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 48px;
          height: 48px;
          flex-shrink: 0;
          background: white;
          border-radius: 10px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
        }

        .report-info {
          flex: 1;
        }

        .report-info h4 {
          margin: 0 0 4px 0;
          font-size: 15px;
          font-weight: 600;
          color: #111827;
        }

        .report-filename {
          margin: 0;
          font-size: 13px;
          color: #6B7280;
          word-break: break-all;
        }

        .report-date {
          margin: 2px 0 0 0;
          font-size: 12px;
          color: #9CA3AF;
        }

        .report-status {
          margin: 0;
          font-size: 13px;
          color: #9CA3AF;
          font-style: italic;
        }

        .report-action {
          flex-shrink: 0;
        }

        .download-button {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 14px;
          background: #10B981;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .download-button:hover:not(:disabled) {
          background: #059669;
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
        }

        .download-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .status-badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
        }

        .status-badge.pending {
          background: #F3F4F6;
          color: #6B7280;
        }

        @media (max-width: 768px) {
          .report-card {
            flex-direction: column;
            text-align: center;
          }

          .report-info {
            text-align: center;
          }

          .download-button {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};